import { useCallback, useEffect, useState } from 'react';
import { ArrowCounterClockwise, Trash, X } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import ConfirmDialog from './ConfirmDialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { useT } from '../i18n/I18nContext';
import { labelColor } from '../lib/labels';
import { extractErrorMessage } from '../lib/errors';

export default function TrashPanel({ open, onOpenChange, projectId, onChange }) {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [emptyConfirmOpen, setEmptyConfirmOpen] = useState(false);
  const [emptyLoading, setEmptyLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/trash`);
      setItems(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('projectBoard.trashLoadFailed')));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const restore = async (ticketId) => {
    try {
      await api.post(`/tickets/${ticketId}/restore`);
      setItems((prev) => prev.filter((tk) => tk.id !== ticketId));
      toast.success(t('projectBoard.trashRestoredToast'));
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('projectBoard.trashRestoreFailedToast')));
    }
  };

  const purge = async () => {
    if (!purgeTarget) return;
    setPurgeLoading(true);
    try {
      await api.delete(`/tickets/${purgeTarget.id}/purge`);
      setItems((prev) => prev.filter((tk) => tk.id !== purgeTarget.id));
      toast.success(t('projectBoard.trashPurgedToast'));
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('projectBoard.trashPurgeFailedToast')));
      throw err;
    } finally {
      setPurgeLoading(false);
    }
  };

  const emptyAll = async () => {
    setEmptyLoading(true);
    try {
      const targets = [...items];
      // Fire requests in parallel — each backend call cascades comments + agent logs.
      const results = await Promise.allSettled(
        targets.map((tk) => api.delete(`/tickets/${tk.id}/purge`)),
      );
      const purged = new Set();
      results.forEach((res, i) => {
        if (res.status === 'fulfilled') purged.add(targets[i].id);
      });
      setItems((prev) => prev.filter((tk) => !purged.has(tk.id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) {
        toast.success(t('projectBoard.trashPurgedToast'));
      } else {
        toast.error(t('projectBoard.trashPurgeFailedToast'));
      }
      onChange?.();
    } finally {
      setEmptyLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="trash-panel"
          className="rounded-sm sm:max-w-2xl max-h-[80vh] flex flex-col p-0 gap-0"
        >
          <DialogHeader className="flex-shrink-0 px-6 pt-5 pb-3 border-b border-zinc-200">
            {/* ``pr-10`` reserves space for the Radix-provided close ``X`` that
                sits absolutely at ``right-4 top-4`` so the "Empty trash" button
                doesn't slide underneath it. */}
            <div className="flex items-center justify-between gap-3 pr-10">
              <div className="flex items-center gap-2 min-w-0">
                <Trash size={18} weight="bold" className="text-zinc-600 flex-shrink-0" />
                <DialogTitle className="font-display text-xl font-black tracking-tight truncate">
                  {t('projectBoard.trashTitle')}
                </DialogTitle>
              </div>
              {items.length > 0 && (
                <Button
                  type="button"
                  data-testid="trash-empty-btn"
                  variant="outline"
                  onClick={() => setEmptyConfirmOpen(true)}
                  className="rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 h-8 text-xs flex-shrink-0"
                >
                  {t('projectBoard.trashEmptyBtn')}
                </Button>
              )}
            </div>
            <DialogDescription className="text-xs text-zinc-500 mt-1">
              {t('projectBoard.trashSubtitle')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="text-center text-xs text-zinc-400 py-8 font-mono uppercase tracking-wider">
                {t('common.loading')}
              </div>
            ) : items.length === 0 ? (
              <div
                data-testid="trash-empty-state"
                className="text-center text-xs text-zinc-400 py-10 font-mono uppercase tracking-wider"
              >
                {t('projectBoard.trashEmpty')}
              </div>
            ) : (
              <ul className="space-y-2" data-testid="trash-list">
                {items.map((tk) => (
                  <li
                    key={tk.id}
                    data-testid={`trash-item-${tk.id}`}
                    className="border border-zinc-200 rounded-sm bg-white p-3 flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-500 mb-1">
                        <span className="text-zinc-900 font-semibold">T#{tk.number}</span>
                        {tk.deleted_at && (
                          <>
                            <span className="text-zinc-300">·</span>
                            <span className="normal-case tracking-normal">
                              {t('projectBoard.trashDeletedAt', {
                                date: new Date(tk.deleted_at).toLocaleString(),
                              })}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-zinc-900 break-words">
                        {tk.title}
                      </div>
                      {(tk.labels || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {tk.labels.map((l) => (
                            <span
                              key={l}
                              className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm ${labelColor(l)}`}
                            >
                              {l}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        data-testid={`trash-restore-${tk.id}`}
                        onClick={() => restore(tk.id)}
                        title={t('projectBoard.trashRestoreBtn')}
                        className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50"
                      >
                        <ArrowCounterClockwise size={11} weight="bold" />
                        {t('projectBoard.trashRestoreBtn')}
                      </button>
                      <button
                        type="button"
                        data-testid={`trash-purge-${tk.id}`}
                        onClick={() => setPurgeTarget(tk)}
                        title={t('projectBoard.trashPurgeBtn')}
                        className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border border-red-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-300"
                      >
                        <X size={11} weight="bold" />
                        {t('projectBoard.trashPurgeBtn')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!purgeTarget}
        onOpenChange={(o) => { if (!o) setPurgeTarget(null); }}
        title={t('projectBoard.trashPurgeConfirmTitle')}
        description={
          purgeTarget
            ? t('projectBoard.trashPurgeConfirmDescription', { number: purgeTarget.number })
            : ''
        }
        confirmLabel={t('projectBoard.trashPurgeBtn')}
        variant="destructive"
        loading={purgeLoading}
        onConfirm={purge}
      />

      <ConfirmDialog
        open={emptyConfirmOpen}
        onOpenChange={setEmptyConfirmOpen}
        title={t('projectBoard.trashEmptyConfirmTitle')}
        description={t('projectBoard.trashEmptyConfirmDescription')}
        confirmLabel={t('projectBoard.trashEmptyBtn')}
        variant="destructive"
        loading={emptyLoading}
        onConfirm={emptyAll}
      />
    </>
  );
}
